import { useTranslate } from "@tolgee/react";
import { useAtom } from "jotai";
import { type ReactNode, useEffect, useState } from "react";
import {
	Dialog,
	DialogCloseButton,
	DialogContent,
	DialogTitle,
	DialogTrigger,
} from "#/components/ui/dialog.tsx";
import {
	FlowIndicator,
	type FlowStatus,
	type FlowStep,
	FlowStepPanel,
	type StepStatus,
} from "#/features/trade/components/order-form/fund-flow-ui.tsx";
import {
	mainWithdrawable,
	useHyperAccount,
} from "#/hooks/use-hyperliquid-account";
import { useUnifiedBalance } from "#/hooks/use-unified-balance";
import { useWallet } from "#/hooks/use-wallet";
import { useWalletBalance } from "#/hooks/use-wallet-balance";
import {
	isTransferPending as isBackendTransferPending,
	useWalletTransfers,
} from "#/hooks/use-wallet-transfers";
import { formatSize, toNumber } from "#/lib/formatters";
import {
	CompletionPreview,
	StepDone,
	TokenChip,
	truncateAddress,
} from "./shared-fund-flow";
import {
	PENDING_WITHDRAWAL_COMPLETED_VISIBLE_MS,
	pendingWithdrawalArrived,
	pendingWithdrawalExpired,
	pendingWithdrawalInTransit,
	pendingWithdrawalsAtom,
} from "./stores/pending-withdrawal";
import { StepExternalWithdraw } from "./withdraw/step-external-withdraw";
import { StepTransferToWallet } from "./withdraw/step-transfer-to-wallet";

type WithdrawDialogProps = {
	children: ReactNode;
	onTransferToWallet?: (amount: string) => Promise<void>;
	onExternalWithdraw?: (payload: {
		amount: string;
		destination: `0x${string}`;
	}) => Promise<unknown>;
};

export function WithdrawDialog({
	children,
	onTransferToWallet,
	onExternalWithdraw,
}: WithdrawDialogProps) {
	const { t } = useTranslate();
	const [open, setOpen] = useState(false);
	const [transferAmount, setTransferAmount] = useState("");
	const [withdrawAmount, setWithdrawAmount] = useState("");
	const [destination, setDestination] = useState("");
	const [isTransferPending, setIsTransferPending] = useState(false);
	const [isWithdrawPending, setIsWithdrawPending] = useState(false);
	const [transferSubmitted, setTransferSubmitted] = useState(false);
	const [success, setSuccess] = useState(false);
	const [txHash, setTxHash] = useState<string | null>(null);
	const [expandedStep, setExpandedStep] = useState<number | null>(1);
	const { accountState } = useHyperAccount();
	const { spotUsdc, isUnified } = useUnifiedBalance();
	const { address } = useWallet();
	const { balance: walletBalance } = useWalletBalance();
	const { data: transfers } = useWalletTransfers();
	const [pendingMap, setPendingMap] = useAtom(pendingWithdrawalsAtom);

	const addrKey = address?.toLowerCase() ?? "";
	const pendingRecord = addrKey ? pendingMap[addrKey] : undefined;
	// Persisted in-flight Step 1 transfer (survives dialog close / refresh).
	// "In transit" until the wallet balance rises above the submit-time baseline.
	const now = Date.now();
	const hasPendingTransfer =
		pendingRecord != null && !pendingWithdrawalExpired(pendingRecord, now);
	const transferInTransit = pendingWithdrawalInTransit(
		pendingRecord,
		walletBalance,
		now,
	);

	const totalMarginUsed = toNumber(
		accountState?.marginSummary?.totalMarginUsed ??
			accountState?.crossMarginSummary?.totalMarginUsed,
	);
	// Step 1 runs withdraw3, which pulls from the MAIN dex only — the merged
	// (main+xyz) withdrawable overstates it and HL rejects the transfer. The
	// spot branch applies only to unified accounts (spot doubles as margin);
	// non-unified spot USDC is not withdraw3-able. See ACCOUNT.md → Cross-DEX.
	const mainW = mainWithdrawable(accountState);
	const tradingWithdrawable =
		isUnified && spotUsdc > 0
			? Math.max(mainW, spotUsdc - totalMarginUsed)
			: mainW;
	// The backend allows exactly one non-terminal transfer per user (partial
	// unique index on wallet_transfers — it's what makes the custodial wallet's
	// nonce a safe source). A second submit 409s, so surface the wait up front
	// instead of letting Step 2 fail with a bare "Withdrawal failed".
	const inflightTransfer = transfers?.some(isBackendTransferPending) ?? false;
	const dialogPending = isTransferPending || isWithdrawPending;
	const withdrawalWalletReady =
		transferSubmitted || hasPendingTransfer || walletBalance > 0 || success;
	const activeStep = withdrawalWalletReady ? 2 : 1;

	useEffect(() => {
		if (!open) return;
		setExpandedStep(activeStep);
	}, [activeStep, open]);

	// Pending-record lifecycle (sole writer besides onSuccess; runs even while
	// closed — the dialog wrapper stays mounted, often as several instances, so
	// every write is guarded to make duplicates no-ops): expired records are
	// dropped; on arrival the record is stamped with `completedAt` instead of
	// deleted, starting the "completed" check window WithdrawStatusIndicator
	// shows; the timer then drops the record — that atom write is what
	// re-renders every reader to hide the check.
	useEffect(() => {
		if (!pendingRecord || !addrKey) return;
		const dropRecord = () =>
			setPendingMap((map) => {
				if (!(addrKey in map)) return map;
				const next = { ...map };
				delete next[addrKey];
				return next;
			});
		if (pendingWithdrawalExpired(pendingRecord, Date.now())) {
			dropRecord();
			return;
		}
		if (!pendingRecord.completedAt) {
			if (pendingWithdrawalArrived(pendingRecord, walletBalance)) {
				setPendingMap((map) => {
					const record = map[addrKey];
					if (!record || record.completedAt) return map;
					return { ...map, [addrKey]: { ...record, completedAt: Date.now() } };
				});
			}
			return;
		}
		const remaining =
			pendingRecord.completedAt +
			PENDING_WITHDRAWAL_COMPLETED_VISIBLE_MS -
			Date.now();
		if (remaining <= 0) {
			dropRecord();
			return;
		}
		const timeoutId = window.setTimeout(dropRecord, remaining);
		return () => window.clearTimeout(timeoutId);
	}, [pendingRecord, walletBalance, addrKey, setPendingMap]);

	function handleOpenChange(nextOpen: boolean) {
		if (dialogPending) return;
		setOpen(nextOpen);
		if (nextOpen) {
			// Resume an in-flight transfer's "in transit" state (survives close /
			// refresh). Amounts reset to empty — the Step 2 input auto-fills with
			// the actual arrived balance (MAX) once funds land, not the sent amount.
			setTransferAmount("");
			setWithdrawAmount("");
			setDestination("");
			setTransferSubmitted(transferInTransit);
			setSuccess(false);
			setTxHash(null);
		}
	}

	const stepStatuses: Record<number, StepStatus> = {
		1: withdrawalWalletReady ? "completed" : "active",
		2: success ? "completed" : activeStep === 2 ? "active" : "upcoming",
	};
	const flowStatuses: Record<string, FlowStatus> = success
		? {
				"trading-account": "origin",
				"withdrawal-wallet": "completed",
				complete: "active",
			}
		: withdrawalWalletReady
			? {
					"trading-account": "origin",
					"withdrawal-wallet": "completed",
					complete: "active",
				}
			: {
					"trading-account": "origin",
					"withdrawal-wallet": "active",
					complete: "upcoming",
				};
	const flowSteps: Array<FlowStep> = [
		{
			id: "trading-account",
			label: t("withdraw.flow.trading-account.label"),
			description: t("withdraw.flow.trading-account.description"),
			status: flowStatuses["trading-account"],
		},
		{
			id: "withdrawal-wallet",
			label: t("withdraw.flow.withdrawal-wallet.label"),
			description: t("withdraw.flow.withdrawal-wallet.description"),
			status: flowStatuses["withdrawal-wallet"],
		},
		{
			id: "complete",
			label: t("withdraw.flow.complete.label"),
			description: t("withdraw.flow.complete.description"),
			status: flowStatuses.complete,
		},
	];

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent className="flex max-h-[calc(100dvh-1rem)] w-[min(480px,calc(100vw-1rem))] flex-col overflow-hidden rounded-[22px] border border-trade-border-subtle bg-[linear-gradient(180deg,var(--trade-surface-overlay),var(--trade-surface-overlay-end))] p-0 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03),0_36px_72px_rgba(0,0,0,0.52)] sm:max-h-[calc(100dvh-2rem)] sm:w-[min(480px,calc(100vw-2rem))]">
				<div className="flex items-start justify-between gap-4 px-5 pt-5">
					<div className="min-w-0">
						<DialogTitle className="text-title1 text-trade-text-strong">
							{t("actions.withdraw")}
						</DialogTitle>
						<p className="mt-1 text-label2-medium text-trade-text-secondary">
							{t("form.asset-network")}
						</p>
						<div className="mt-3">
							<FlowIndicator steps={flowSteps} />
						</div>
					</div>
					<DialogCloseButton disabled={dialogPending} />
				</div>

				<div className="min-h-0 overflow-y-auto px-5 pb-5 pt-4">
					<div className="flex flex-col gap-3">
						<FlowStepPanel
							step={1}
							title={t("withdraw.title-step1")}
							status={stepStatuses[1]}
							expanded={expandedStep === 1}
							onToggle={() => setExpandedStep((c) => (c === 1 ? null : 1))}
							preview={
								<TradingAccountPreview
									address={address}
									amount={transferAmount}
									withdrawable={tradingWithdrawable}
								/>
							}
						>
							<StepTransferToWallet
								amount={transferAmount}
								isPending={isTransferPending}
								readOnly={success}
								withdrawable={tradingWithdrawable}
								onAmountChange={setTransferAmount}
								onConfirm={onTransferToWallet}
								onSuccess={() => {
									setTransferSubmitted(true);
									// Persist the in-flight transfer so the "in transit"
									// state + Step 2 survive dialog close / page refresh.
									// The Step 2 amount is intentionally NOT prefilled here —
									// it auto-fills with the actual arrived balance (MAX) once
									// funds land, avoiding the $1-fee mismatch (sent 100 →
									// arrived ~99 would otherwise read as "exceeds balance").
									if (addrKey) {
										setPendingMap((map) => ({
											...map,
											[addrKey]: {
												amount: transferAmount,
												balanceBefore: walletBalance,
												ts: Date.now(),
											},
										}));
									}
								}}
								setIsPending={setIsTransferPending}
							/>
						</FlowStepPanel>

						<FlowStepPanel
							step={2}
							title={
								success ? t("withdraw.title-step3") : t("withdraw.title-step2")
							}
							status={stepStatuses[2]}
							expanded={expandedStep === 2}
							onToggle={() =>
								setExpandedStep((c) => (c === 2 ? (success ? 2 : null) : 2))
							}
							preview={
								success ? (
									<CompletionPreview
										successMessage={t("withdraw.success-message")}
										txHash={txHash}
									/>
								) : (
									<WithdrawalWalletPreview
										address={address}
										walletBalance={walletBalance}
									/>
								)
							}
						>
							{success ? (
								<StepDone
									onClose={() => setOpen(false)}
									successMessage={t("withdraw.success-message")}
									titleKey="withdraw.title-step3"
									txHash={txHash}
								/>
							) : (
								<StepExternalWithdraw
									amount={withdrawAmount}
									destination={destination}
									inflightTransfer={inflightTransfer}
									isPending={isWithdrawPending}
									readOnly={!withdrawalWalletReady}
									transferPending={transferInTransit}
									walletBalance={walletBalance}
									onAmountChange={setWithdrawAmount}
									onDestinationChange={setDestination}
									onConfirm={onExternalWithdraw}
									onSuccess={(result) => {
										setTxHash(extractTransactionHash(result));
										setSuccess(true);
									}}
									setIsPending={setIsWithdrawPending}
								/>
							)}
						</FlowStepPanel>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}

function TradingAccountPreview({
	address,
	amount,
	withdrawable,
}: {
	address?: string | null;
	amount: string;
	withdrawable: number;
}) {
	return (
		<div className="flex flex-wrap items-center gap-2">
			<TokenChip
				iconSrc="https://sdk-cdn.fun.xyz/images/usdc.svg"
				label="USDC"
			/>
			<span className="text-caption1-medium text-trade-text-strong">
				{amount.length > 0
					? `${amount} USDC`
					: `${formatSize(withdrawable, 6)} USDC`}
			</span>
			{address && (
				<span className="truncate rounded-full bg-trade-surface-inset px-2 py-1 text-caption1-medium text-trade-text-secondary">
					{truncateAddress(address)}
				</span>
			)}
		</div>
	);
}

function WithdrawalWalletPreview({
	address,
	walletBalance,
}: {
	address?: string | null;
	walletBalance: number;
}) {
	return (
		<div className="flex flex-wrap items-center gap-2">
			<TokenChip
				iconSrc="https://sdk-cdn.fun.xyz/images/usdc.svg"
				label="USDC"
			/>
			<TokenChip
				iconSrc="https://sdk-cdn.fun.xyz/images/arbitrum.svg"
				label="Arbitrum"
			/>
			<span className="text-caption1-medium text-trade-text-strong">
				{formatSize(walletBalance, 2)} USDC
			</span>
			{address && (
				<span className="truncate rounded-full bg-trade-surface-inset px-2 py-1 text-caption1-medium text-trade-text-secondary">
					{truncateAddress(address)}
				</span>
			)}
		</div>
	);
}

function extractTransactionHash(result: unknown) {
	if (!result || typeof result !== "object") return null;
	const maybeHash =
		"transactionHash" in result
			? result.transactionHash
			: "hash" in result
				? result.hash
				: null;
	return typeof maybeHash === "string" ? maybeHash : null;
}
